/**
 * <dex-app-shell> — the consumer-facing entry point.
 *
 *   <script src="https://shell.dexcare.ai/v1/shell.js"></script>
 *   <dex-app-shell app="claims-explorer">
 *     ...your nav and content...
 *   </dex-app-shell>
 *
 * A consuming app declares only WHICH app it is. Everything else — the tile
 * list, the org, entitlements — resolves at runtime from the platform.
 *
 * Two properties are non-negotiable and are why this file is defensive to the
 * point of paranoia:
 *
 *  1. It NEVER blocks the host app. If the registry is unreachable, the bundle
 *     half-loads, or an attribute is malformed, the app's own content still
 *     renders. Chrome degrades; it does not take the page down. A shared bundle
 *     is a shared blast radius, so this is the price of serving it centrally.
 *  2. It does not own login. The OAuth redirect must return to the consuming
 *     app's origin, so each app keeps its own Auth0 client and /callback, and
 *     hands us a token getter. That also means auth is independent of, and prior
 *     to, adopting the shell.
 */
import { createRoot, type Root } from "react-dom/client"
import { createElement } from "react"
import css from "./shell.css?inline"
import { TopBar } from "./TopBar"
import { getOrg, loadOrg, setOrg, type OrgInput } from "./org"
import {
  buildHref,
  hasHosts,
  hostsForEnvironment,
  environmentFromHostname,
  needsOrg,
  orgIdFromUrl,
  resolveTiles,
  type Environment,
  type HostMap,
  type Tile,
} from "./registry"
import { forgetTenants, loadTenants, type Tenant, type TenantsFailure } from "./tenants"

/**
 * A launcher tile with its destination already worked out — what a host needs
 * to list the other apps somewhere of its own.
 */
export interface ResolvedTile {
  key: string
  label: string
  group: "Apps" | "Manage"
  /** Absolute URL, or null when it cannot be built yet — see `disabled`. */
  href: string | null
  isExternal: boolean
  /** This is the app currently rendering the shell. */
  active: boolean
  /** Tenant-scoped, but no organization is known yet. Show it, don't follow it. */
  disabled: boolean
}

export interface ShellConfig {
  /** Supply the host app's access token so the shell can read the platform registry. */
  getToken?: () => Promise<string | null>
  /**
   * Which deployment this app is — `"production"` (default) or `"development"`.
   *
   * Selects the per-environment host map, so a dev app's launcher points at dev
   * apps instead of being a door into production. Explicit rather than sniffed
   * from `location.hostname`: a wrong guess here moves someone between
   * environments, and this shell has already shipped that bug once.
   *
   * An app with no deployment in the chosen environment renders DISABLED rather
   * than falling back to another environment's URL.
   *
   * NOT `tenant.tier` — that labels a customer, this labels a deployment.
   */
  environment?: Environment
  /**
   * Override app hosts. Merged OVER the environment map, so a local or one-off
   * host wins over both — which is how you point at a branch deployment without
   * teaching the bundle about it.
   */
  hosts?: HostMap
  /** Platform registry endpoint. When unset, the embedded seed is used. */
  registryUrl?: string
  /**
   * The platform API root, e.g. `https://api-dexhive.prod.optimize.dex.care`
   * (production) or `https://api-dexhive.dev.optimize.dex.care` (development).
   *
   * The one thing to set: the shell derives both the app registry and the tenant
   * list from it. No host is baked in, because the API host is environment-specific
   * and inventing one is how six launcher tiles came to point at a domain that did
   * not exist.
   */
  apiBaseUrl?: string
  /**
   * Where to read the user's organizations — `GET /me/tenants`. Only needed if it
   * is not `${apiBaseUrl}/me/tenants`.
   *
   * Without any of these the tenant switcher degrades to a link to the full
   * organizations page.
   *
   * Pointing at the PLATFORM needs `getToken` and needs the calling origin on the
   * platform CORS allowlist (AIE-542).
   *
   * Pointing at a path on the HOST'S OWN ORIGIN — `/api/shell/tenants`, answering
   * in `/me/tenants` shape — needs neither: the request carries cookies, so a
   * server-rendered app with an httpOnly session can populate the switcher without
   * exposing a token to JavaScript.
   */
  tenantsUrl?: string
  /**
   * The organization the host app is showing.
   *
   * Required for any app that does not carry `?org=` in its URL — which is most
   * of them, since Optimize routes on `/tenant/<uuid>` and DMT on `/org/<uuid>`.
   * Pass `{ id, name }`; a bare id works but labels the org with its UUID.
   *
   * Call again on every client-side org change. `null` clears it (sign-out).
   */
  org?: OrgInput | null
}

const config: Omit<ShellConfig, "org"> = {}

/** Global configuration hook, called by the host before/around mount. */
const DexShell = {
  configure(next: ShellConfig) {
    const { org, ...rest } = next
    // `in`, not a truthiness check: `org: null` is an explicit clear, while
    // omitting the key entirely must leave a good org alone.
    if ("org" in next) setOrg(org)
    // An explicit clear means the session changed, so the cached organization
    // list belongs to whoever was signed in before.
    if ("org" in next && org === null) forgetTenants()
    const tokenChanged = "getToken" in rest && rest.getToken !== config.getToken
    Object.assign(config, rest)
    // A host that hands us a token late — the common case, since auth resolves
    // after first paint — is the moment the tenant list becomes fetchable.
    const endpointChanged =
      "tenantsUrl" in rest || "apiBaseUrl" in rest || "registryUrl" in rest
    if (tokenChanged || endpointChanged) forgetTenants()
    // Re-render anything already mounted so late configuration still applies.
    for (const el of mounted) {
      if (tokenChanged || endpointChanged) void el.hydrateTenants()
      // A late org is the common case — auth resolves after first paint — and the tile
      // list is entitlement-filtered per tenant, so it has to be asked again.
      if (tokenChanged || endpointChanged || "org" in next) {
        void el.maybeRehydrateRegistry(tokenChanged || endpointChanged)
      }
      el.render()
    }
  },
  /** What the shell currently believes, org included. For debugging in a console. */
  get config(): ShellConfig {
    return { ...config, org: getOrg() }
  },

  /**
   * The launcher tiles as the shell currently sees them, hrefs resolved.
   *
   * For hosts that surface cross-app navigation somewhere the shell does not
   * own — a command palette, a footer, a mobile menu. Without it, the only way
   * to list the other apps is to hand-copy the registry, which is the clone
   * this package exists to stop: Claims Explorer's ⌘K did exactly that, and
   * its copy shipped six tiles pointing at a host that did not exist.
   *
   * Empty until a `<dex-app-shell>` is connected, and again if one never is —
   * callers should treat an empty list as "no launcher", not as an error. The
   * result is a snapshot, not live: read it when you open your menu, because
   * the registry hydrates asynchronously and the org can change under you.
   */
  tiles(): ResolvedTile[] {
    // First mounted element. More than one shell on a page is already a
    // misconfiguration (they would render two bars); picking the first keeps
    // this total rather than adding a second way to fail.
    const el = mounted.values().next().value
    if (!el) return []
    return el.resolvedTiles()
  },
}

const mounted = new Set<DexAppShellElement>()

class DexAppShellElement extends HTMLElement {
  static observedAttributes = [
    "app",
    "registry-url",
    "hosts",
    "org",
    "org-name",
    "org-info-url",
    "tenants-url",
    "api-base-url",
    "environment",
  ]

  private root: Root | null = null
  private mountPoint: HTMLDivElement | null = null
  private tiles: Tile[] = []
  private degraded = false
  private tenants: Tenant[] = []
  private tenantsFailure: TenantsFailure | undefined
  /** Which org the current tile list was fetched for. `undefined` = never fetched. */
  private registryOrgId: string | null | undefined = undefined

  connectedCallback() {
    mounted.add(this)
    try {
      this.build()
      // Attributes first: an org the host declared outranks anything in the URL,
      // and loadOrg() honours that. (For markup parsed from HTML this has already
      // run via attributeChangedCallback; it is idempotent.)
      this.applyOrgAttributes()
      loadOrg()
      this.render()
      void this.hydrateRegistry()
      void this.hydrateTenants()
    } catch (e) {
      // Rule 1. Something in our own setup broke — surface the app anyway.
      this.failOpen(e)
    }
  }

  disconnectedCallback() {
    mounted.delete(this)
    try {
      this.root?.unmount()
    } catch {
      /* nothing useful to do */
    }
    this.root = null
  }

  attributeChangedCallback(name: string) {
    if (name === "org" || name === "org-name") this.applyOrgAttributes()
    if ((name === "tenants-url" || name === "api-base-url") && this.root) {
      forgetTenants()
      void this.hydrateTenants()
    }
    // `org-name` is excluded deliberately: a relabelling does not change entitlements,
    // and refetching on it would double every request from a host that sets both.
    if (name === "org" || name === "registry-url" || name === "api-base-url") {
      if (this.root) void this.maybeRehydrateRegistry(name !== "org")
    }
    if (this.root) this.render()
  }

  /**
   * `<dex-app-shell org="<uuid>" org-name="Acme Health">`.
   *
   * Both are read together on every change, because the parser delivers them one
   * at a time and reading only the one that fired would settle on a UUID label.
   *
   * A missing or blank `org` is left alone rather than treated as a clear: a host
   * binding `org={tenantId}` renders once before its tenant loads, and dropping a
   * good org on that render would put the generic mark back on screen.
   */
  private applyOrgAttributes() {
    const id = this.getAttribute("org")
    if (id) {
      setOrg({
        id,
        name: this.getAttribute("org-name"),
        infoUrl: this.getAttribute("org-info-url"),
      })
    }
  }

  /** Shadow root: chrome styles must not leak into the host page, and the host's
   *  CSS must not silently restyle the platform bar. */
  private build() {
    const shadow = this.attachShadow({ mode: "open" })
    const style = document.createElement("style")
    style.textContent = css
    shadow.append(style)

    this.mountPoint = document.createElement("div")
    shadow.append(this.mountPoint)

    // Default slot — the host app's own nav and content, rendered below the bar.
    const content = document.createElement("slot")
    shadow.append(content)
  }

  /** Last resort: expose the host's content with no chrome rather than nothing. */
  private failOpen(e: unknown) {
    console.error("[dex-app-shell] failed to initialise; rendering without chrome.", e)
    try {
      if (!this.shadowRoot) this.attachShadow({ mode: "open" })
      const shadow = this.shadowRoot!
      shadow.textContent = ""
      shadow.append(document.createElement("slot"))
    } catch {
      /* if even this fails the element is inert, which still leaves the page usable */
    }
  }

  render() {
    if (!this.mountPoint) return
    try {
      this.root ??= createRoot(this.mountPoint)
      this.root.render(
        createElement(TopBar, {
          app: this.getAttribute("app") ?? "",
          tiles: this.tiles,
          hosts: this.resolvedHosts(),
          degraded: this.degraded,
          onNavigate: this.requestAppNavigate,
          onOrgSwitch: this.requestOrgSwitch,
          orgSwitchIsExternal: this.orgSwitchIsExternal,
          tenants: this.tenants,
          tenantsFailure: this.tenantsFailure,
        }),
      )
    } catch (e) {
      this.failOpen(e)
    }
  }

  /**
   * Fetch the user's organizations for the switcher, then re-render.
   *
   * Never throws — an unreachable list means the switcher shows a link to the
   * full organizations page, which is exactly what existed before it.
   */
  async hydrateTenants() {
    const url = this.tenantsEndpoint()
    const { tenants, error, reason } = await loadTenants(url, config.getToken)
    this.tenants = tenants
    this.tenantsFailure = reason
    if (error) {
      // Always say something. The first version only warned when a host had set
      // tenantsUrl, so the most common failure — never setting it — was silent,
      // and the switcher just looked broken.
      console.warn(
        `[dex-app-shell] tenant switcher unavailable: ${error}. ` +
          "Set apiBaseUrl (or tenantsUrl) via DexShell.configure, plus getToken — or, if " +
          "your session lives in a cookie rather than a token you can read, point " +
          'tenants-url at your own origin ("/api/shell/tenants") answering in /me/tenants shape.',
      )
    }
    this.adoptPlatformOrgName()
    this.render()
  }

  /**
   * Where to read organizations from.
   *
   * Derived from `apiBaseUrl` — or from `registryUrl`, since that already names
   * the platform API — so adopting the switcher is ONE piece of configuration
   * rather than a third thing to remember. No host is baked in: the platform API
   * host is environment-specific and inventing one is how six launcher tiles
   * ended up pointing at a domain that did not exist.
   */
  private tenantsEndpoint(): string | null {
    const explicit = this.getAttribute("tenants-url") ?? config.tenantsUrl
    if (explicit) return explicit

    const base = config.apiBaseUrl ?? this.getAttribute("api-base-url") ?? null
    if (base) return `${base.replace(/\/$/, "")}/me/tenants`

    // `https://api.example/platform/v1/apps` → `https://api.example/me/tenants`
    const registry = this.getAttribute("registry-url") ?? config.registryUrl
    if (registry) {
      try {
        return new URL("/me/tenants", registry).toString()
      } catch {
        /* not a usable URL; fall through to unconfigured */
      }
    }
    return null
  }

  /**
   * Where to read the launcher tiles from — `GET /platform/v1/apps`.
   *
   * Derived from `apiBaseUrl`, the same way the tenant list is, because otherwise
   * setting the one thing the docs tell you to set gets you a working switcher and a
   * launcher still quietly serving the bundled seed. That is the worst kind of
   * half-configured: everything renders, nothing is wrong on screen, and the tile
   * list is simply months out of date.
   */
  private registryEndpoint(): string | null {
    const explicit = this.getAttribute("registry-url") ?? config.registryUrl
    if (explicit) return explicit

    const base = config.apiBaseUrl ?? this.getAttribute("api-base-url") ?? null
    if (base) return `${base.replace(/\/$/, "")}/platform/v1/apps`

    return null
  }

  /**
   * Re-fetch the tiles if the organization has changed since we last did.
   *
   * The platform filters tiles by the caller's entitlements *in a given tenant*, so a
   * new org is a different answer rather than the same list relabelled — someone who
   * owns one tenant and is a plain member of another must not carry the first
   * tenant's Admin tile into the second.
   *
   * `force` covers a changed token or endpoint, where the org is the same but the
   * answer may not be.
   */
  async maybeRehydrateRegistry(force = false) {
    if (!force && (getOrg()?.id ?? null) === this.registryOrgId) return
    await this.hydrateRegistry()
  }

  /**
   * Take the organization's real name from the platform once we know it.
   *
   * A host that renders `<dex-app-shell org={tenantId}>` — the obvious thing to
   * write — gave us an id and no name, and the bar then displayed a raw UUID.
   * That was a design mistake: the tenant list has the answer, so the shell
   * should not depend on the host repeating it.
   *
   * Only fills a name in when there isn't a real one, so a host that deliberately
   * labels an org differently keeps its label.
   */
  private adoptPlatformOrgName() {
    const org = getOrg()
    if (!org?.id) return
    if (org.name && org.name !== org.id) return // a real name is already set

    const match = this.tenants.find((t) => t.id === org.id)
    if (match?.name && match.name !== org.id) {
      // Carry infoUrl through: setOrg rebuilds the Org from its input, so
      // omitting it here would drop a logo the host had already handed over.
      setOrg({ id: org.id, name: match.name, infoUrl: org.infoUrl })
      return
    }

    // Still nothing but an id, so the bar is about to display a raw UUID where a
    // name belongs. Say so once: this exact symptom was reported as "the switcher
    // shows the org id", and a silent visual defect is the hardest kind to trace.
    if (!this.warnedAboutOrgName) {
      this.warnedAboutOrgName = true
      console.warn(
        `[dex-app-shell] showing the organization id ("${org.id}") because no name is known. ` +
          "Either pass org-name, or configure apiBaseUrl + getToken so the shell can read " +
          "the name from /me/tenants.",
      )
    }
  }

  private warnedAboutOrgName = false

  /**
   * Dispatch `dex-org-switch`, returning true when a host called preventDefault
   * and handled it. `composed` is required for the event to leave the shadow
   * root.
   *
   * `detail.target` is the tenant the user picked in the switcher, or undefined
   * when they only asked for the full list. `detail.org` stays the CURRENT
   * organization — the contract published before the switcher existed — so a
   * host already handling this event keeps working and can adopt `target` when
   * it wants the in-app fast path.
   */
  /**
   * Dispatch `dex-app-navigate` when a launcher tile is picked, returning true
   * when a host called preventDefault and routed itself.
   *
   * Why this exists: the shell can only navigate with `window.location.assign`,
   * which is a full page load. For a tile that points back at the host's OWN
   * origin — every in-shell tile in Optimize, since Optimize is what serves them
   * — that discards the running app to render a route it could have pushed in a
   * few milliseconds. A SPA gets first refusal here so it can route in place.
   *
   * `isExternal` is in the detail because a host must NOT swallow a tile aimed at
   * another product: handing "https://dmt.example/org/x" to a client-side router
   * would be routed as a path and go nowhere. Check it, and check the origin.
   *
   * Unhandled behaves exactly as before — new tab for external, full load for
   * internal — so this is additive for every existing consumer.
   */
  private requestAppNavigate = (tile: Tile, href: string): boolean => {
    try {
      const event = new CustomEvent("dex-app-navigate", {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: {
          key: tile.key,
          label: tile.label,
          href,
          isExternal: tile.isExternal,
          org: getOrg(),
          app: this.getAttribute("app") ?? "",
        },
      })
      return !this.dispatchEvent(event)
    } catch (e) {
      // Same posture as dex-org-switch: a throwing listener must not strand the
      // user on a menu row that did nothing, so fall through to navigating.
      console.error(
        "[dex-app-shell] a dex-app-navigate listener threw; navigating directly instead.",
        e,
      )
      return false
    }
  }

  private requestOrgSwitch = (target?: Tenant): boolean => {
    try {
      const event = new CustomEvent("dex-org-switch", {
        bubbles: true,
        composed: true,
        cancelable: true,
        detail: { org: getOrg(), app: this.getAttribute("app") ?? "", target },
      })
      const handled = !this.dispatchEvent(event)
      // The arrow describes the "All organizations" row, so only a call about
      // that row may change it. A host that handles tenant *picks* in-app but
      // still sends people to Optimize for the full list would otherwise lose
      // the affordance telling them they are about to leave.
      if (!target && handled !== !this.orgSwitchIsExternal) {
        this.orgSwitchIsExternal = !handled
        this.render()
      }
      return handled
    } catch (e) {
      console.error("[dex-app-shell] a dex-org-switch listener threw; falling back to Optimize.", e)
      return false
    }
  }

  private orgSwitchIsExternal = true

  /**
   * Backing implementation of `DexShell.tiles()`.
   *
   * Deliberately mirrors what the waffle itself does — the same `needsOrg`
   * gate, the same `buildHref`, the same hosts merge — so a host's own menu
   * cannot disagree with the launcher sitting above it. A template that throws
   * yields `href: null` rather than propagating: one bad tile must not take out
   * the caller's whole menu.
   */
  /**
   * The host map in force: environment base, then configured overrides, then the
   * `hosts=""` attribute.
   *
   * ONE method because the waffle and `DexShell.tiles()` both need it and a host
   * menu that disagreed with the launcher above it would be worse than either.
   * They were two copies of this expression; environments gave them a second
   * thing to drift on.
   */
  private resolvedHosts(): HostMap {
    const environment =
      this.getAttribute("environment") ??
      config.environment ??
      environmentFromHostname(window.location.hostname)
    return {
      ...hostsForEnvironment(environment),
      ...config.hosts,
      ...this.hostsFromAttribute(),
    }
  }

  resolvedTiles(): ResolvedTile[] {
    const app = this.getAttribute("app") ?? ""
    const org = getOrg()
    const hosts = this.resolvedHosts()
    return this.tiles.map((tile) => {
      const disabled = (needsOrg(tile) && !org?.id) || !hasHosts(tile, hosts)
      let href: string | null = null
      if (!disabled) {
        try {
          href = buildHref(tile, org ?? {}, hosts)
        } catch (e) {
          console.error("[dex-app-shell]", e)
        }
      }
      return {
        key: tile.key,
        label: tile.label,
        group: tile.group,
        href,
        isExternal: tile.isExternal,
        active: tile.key === app,
        disabled,
      }
    })
  }

  private hostsFromAttribute(): HostMap {
    const raw = this.getAttribute("hosts")
    if (!raw) return {}
    try {
      return JSON.parse(raw) as HostMap
    } catch {
      console.warn('[dex-app-shell] ignoring malformed hosts="" attribute (expected JSON).')
      return {}
    }
  }

  /** Fetch the platform tile list, then re-render. Falls back to the seed. */
  private async hydrateRegistry() {
    const url = this.registryEndpoint()
    // Captured before the await, and recorded before it too: a second call for a
    // different org must not look like a duplicate of this one while it is in flight.
    const forOrg = getOrg()?.id ?? null
    this.registryOrgId = forOrg
    const { tiles, source, error } = await resolveTiles(url, config.getToken, forOrg)
    this.tiles = tiles
    this.degraded = source === "seed" && !!url
    if (error) {
      console.warn(`[dex-app-shell] using the built-in app list: ${error}`)
    }

    // Now that the tiles are known, the app's own href_template can be run
    // backwards against the current URL. Only possible here, not at connect time,
    // because it needs the registry.
    this.adoptOrgFromUrl()
    this.render()

    // Deriving the org from the URL is chicken-and-egg: it needs the templates, which
    // means the fetch above necessarily ran without a tenant and came back filtered to
    // only the tiles everyone can see. Now that the org is known, ask again — otherwise
    // a path-routed app permanently shows the unentitled subset. One extra request on
    // first load, and only for the apps that have no other way to learn their org.
    if ((getOrg()?.id ?? null) !== forOrg) {
      await this.maybeRehydrateRegistry()
      return
    }

    // A full async tick has passed, so a host that was going to hand over an org
    // has had its chance. Say so once, here, rather than letting it surface as a
    // generic org mark and dead launcher tiles that nobody traces back.
    if (!getOrg()) {
      console.warn(
        "[dex-app-shell] no organization. Cross-app links need a tenant id, so " +
          'the launcher is disabled until one arrives. Pass org="<uuid>" ' +
          "org-name=\"…\" on <dex-app-shell>, or call DexShell.configure({ org: { id, name } }) " +
          "— apps that route on a path (/tenant/<uuid>, /org/<uuid>) have no ?org= for the shell to read.",
      )
    }
  }

  /**
   * Last resort before showing the generic mark: read the tenant id out of the
   * URL using this app's own registry template.
   *
   * Runs only when nothing better is known, so it never overrides a host-supplied
   * org, a `?org=` handoff, or a remembered one. The name is filled in later by
   * `adoptPlatformOrgName` once the tenant list arrives.
   */
  private adoptOrgFromUrl() {
    if (getOrg()) return
    const app = this.getAttribute("app") ?? ""
    const id = orgIdFromUrl(app, this.tiles, window.location.pathname)
    if (id) setOrg({ id })
  }
}

// Guard against double-registration: two copies of the bundle on one page must
// not throw, which would take the host app down with it.
if (!customElements.get("dex-app-shell")) {
  customElements.define("dex-app-shell", DexAppShellElement)
}

declare global {
  interface Window {
    DexShell?: typeof DexShell
  }
}
window.DexShell ??= DexShell

export { DexShell }
